import type { UpdateUserRequest, UserPublic } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { STR } from '../../i18n';
import { ApiError, hasErrorCode, setUserHr, setUserIt, updateUser } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { useSession } from '../../lib/session';
import { landingPath } from '../../lib/surfaces';
import { showToast } from '../../lib/toast';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../ui/Modal';
import { Callout } from '../../ui/Primitives';
import {
  checkboxRowStyle,
  checkboxStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  textFieldStyle,
} from '../../ui/styles';

import { RefusalModal } from './SharedModals';

/**
 * C6c — THE EDIT USER DIALOG, as the design of record draws it: name, email, then title and
 * department side by side, then the administrator box. It is one dialog, opened from every
 * affordance that edits a person — the detail pane's Edit button and the rail's own avatar —
 * so the two can never drift apart.
 *
 * ONE WRITE. Everything on this form, the administrator checkbox included, travels in a
 * single `PATCH /api/admin/users/:id`; the server routes the role change into its guarded
 * admin-toggle path. The last-active-admin refusal below therefore fires on the server's
 * ACTUAL 409, never on a client-side count of who the last administrator is.
 *
 * SELF-DEMOTION IS ALLOWED, and confirmed rather than refused (operator ruling). Removing
 * your own role costs you the panel immediately, so the consequence is named in plain words
 * first — and the session query is invalidated afterwards so the client's own view of
 * `isAdmin` cannot go stale and leave a shell whose every read would 403.
 */

type Stage =
  | { kind: 'form' }
  | { kind: 'confirmSelfDemote'; patch: UpdateUserRequest }
  | { kind: 'refuseDemote' };

export function EditUserDialog({ user, onClose }: { user: UserPublic; onClose: () => void }) {
  const queryClient = useQueryClient();
  const session = useSession();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [title, setTitle] = useState(user.title ?? '');
  const [department, setDepartment] = useState(user.department ?? '');
  const [isAdmin, setIsAdmin] = useState(user.isAdmin);
  /*
   * THE HR FLAG (SIR-7460 decision 1) — an ADDED box, admins only. It is its own write
   * (`POST /api/admin/users/:id/hr`, admin-gated, audited `users.grant_hr`/`revoke_hr`), so
   * it is sent first and the existing one-write PATCH follows unchanged when anything else
   * changed too.
   */
  const [isHr, setIsHr] = useState(user.isHr);
  const [isIt, setIsIt] = useState(user.isIt);
  const actorIsAdmin = session.data?.isAdmin === true;
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'form' });

  const navigate = useNavigate();
  const isSelf = session.data?.id === user.id;

  /*
   * D2 — REACTIVATE BEFORE PROMOTE. The server refuses promoting a deactivated user with
   * 400 `inactive_target`, exactly as it refuses a grant into a deactivated application, so
   * an enabled promote control here would be an affordance promising what the decision layer
   * will refuse. DEMOTION IS NEVER BLOCKED, which is the whole asymmetry: the box is sealed
   * only when the sole move it could make is a promotion — the person is deactivated AND is
   * not already an administrator. A deactivated administrator keeps a live box and can be
   * demoted with it. This is an affordance, not the decision: the server still decides, and
   * the refusal below still surfaces the server's own words if one ever arrives anyway.
   */
  const promotionBlocked = !user.active && !user.isAdmin;

  const mutation = useMutation({
    mutationFn: (patch: UpdateUserRequest) => updateUser(user.id, patch),
    onSuccess: (result, patch) => {
      const selfDemotion = isSelf && patch.isAdmin === false;
      /*
       * `me` always: this dialog can re-label the acting administrator's own name and
       * address, which the shell renders. On a SELF-DEMOTION it is load-bearing — the guard
       * and the rail both derive from this query, so without it the panel would keep
       * rendering for somebody the server has already stopped authorizing.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.me }).then(() => {
        /*
         * A self-demotion also DROPS every admin-wide Queue entry — the chrome count's, the
         * bell's `type=request` one, the Queue's — once the fresh session is in. An admin who
         * is also HR keeps the Access surfaces, now on HR's narrower Queue; its key carries
         * the scope, so the fresh session already reads its own entry, and this removes the
         * admin-wide rows from memory as well, so nothing can ever render them for HR.
         */
        if (selfDemotion) {
          queryClient.removeQueries({ queryKey: queryKeys.accessQueueScope('admin') });
        }
      });
      /*
       * …but the admin lists are deliberately NOT invalidated on a self-demotion. Those
       * queries still have live observers for the beat before the guard unmounts the screen,
       * and refetching them would fire admin reads the server now correctly 403s. The lists
       * are about to become unreachable anyway; leaving them alone means the transition
       * issues no refused request at all.
       */
      if (!selfDemotion) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.users });
        void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
      }
      if (patch.isAdmin === undefined) showToast(STR.toast.userUpdated(result.user.name));
      else if (patch.isAdmin) showToast(STR.toast.nowAdmin(result.user.name));
      else showToast(STR.toast.nowMember(result.user.name));
      onClose();
      /*
       * A self-demotion has to LEAVE the admin panel, not merely stop rendering it. The
       * confirmation promises "you go back to your account", and without this the person
       * stays parked on /admin/* watching a refusal for a page they were already on — the
       * copy and the behaviour disagreeing. Navigate home, after the invalidation above so
       * the guard resolves against the fresh session rather than the stale one.
       */
      if (selfDemotion) navigate(landingPath(), { replace: true });
    },
    onError: (failure) => {
      if (hasErrorCode(failure, 'last_active_admin')) {
        setStage({ kind: 'refuseDemote' });
        return;
      }
      setStage({ kind: 'form' });
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const hrMutation = useMutation({
    mutationFn: (next: boolean) => setUserHr(user.id, next),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
      showToast(
        result.user.isHr
          ? STR.access.person.toast.hrOn(result.user.name)
          : STR.access.person.toast.hrOff(result.user.name),
      );
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const itMutation = useMutation({
    mutationFn: (next: boolean) => setUserIt(user.id, next),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
      showToast(
        result.user.isIt
          ? STR.access.person.toast.itOn(result.user.name)
          : STR.access.person.toast.itOff(result.user.name),
      );
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  /** Only what actually changed. The server requires at least one field. */
  function buildPatch(): UpdateUserRequest | null {
    const patch: UpdateUserRequest = {};
    if (name.trim() !== user.name) patch.name = name.trim();
    if (email.trim() !== user.email) patch.email = email.trim();
    if (title.trim() !== (user.title ?? '')) patch.title = title.trim();
    if (department.trim() !== (user.department ?? '')) patch.department = department.trim();
    if (isAdmin !== user.isAdmin) patch.isAdmin = isAdmin;
    return Object.keys(patch).length === 0 ? null : patch;
  }

  function submit() {
    if (name.trim() === '' || email.trim() === '') {
      setError(STR.modals.editUser.required);
      return;
    }
    const patch = buildPatch();
    const hrChanged = actorIsAdmin && isHr !== user.isHr;
    const itChanged = actorIsAdmin && isIt !== user.isIt;
    if (patch === null && !hrChanged && !itChanged) {
      setError(STR.modals.editUser.unchanged);
      return;
    }
    setError(null);
    const finish = () => {
      if (patch === null) onClose();
      else proceed(patch);
    };
    const updateItThenFinish = () => {
      if (itChanged) itMutation.mutate(isIt, { onSuccess: finish });
      else finish();
    };
    if (hrChanged) hrMutation.mutate(isHr, { onSuccess: updateItThenFinish });
    else updateItThenFinish();
  }

  /** The existing path for the PATCH, unchanged. */
  function proceed(patch: UpdateUserRequest) {
    if (isSelf && user.isAdmin && patch.isAdmin === false) {
      setStage({ kind: 'confirmSelfDemote', patch });
      return;
    }
    mutation.mutate(patch);
  }

  if (stage.kind === 'refuseDemote') {
    return (
      <RefusalModal
        title={STR.modals.refuseDemote.title(user.name)}
        strong={STR.modals.refuseDemote.strong}
        body={STR.modals.refuseDemote.body}
        after={STR.modals.refuseDemote.after}
        onClose={onClose}
      />
    );
  }

  if (stage.kind === 'confirmSelfDemote') {
    const { patch } = stage;
    return (
      <Modal
        title={STR.modals.confirmSelfDemote.title}
        onClose={() => {
          setStage({ kind: 'form' });
        }}
        footer={
          <>
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              onClick={() => {
                setStage({ kind: 'form' });
              }}
            >
              {STR.common.cancel}
            </button>
            <button
              type="button"
              className="dbtn"
              style={modalButtonStyle}
              disabled={mutation.isPending}
              onClick={() => {
                mutation.mutate(patch);
              }}
            >
              {STR.modals.confirmSelfDemote.submit}
            </button>
          </>
        }
      >
        <ModalProse>
          <ModalParagraph>{STR.modals.confirmSelfDemote.body}</ModalParagraph>
          <p className="tx-muted" style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.6 }}>
            {STR.modals.confirmSelfDemote.after}
          </p>
        </ModalProse>
      </Modal>
    );
  }

  return (
    <Modal
      title={STR.modals.editUser.title}
      lead={STR.modals.editUser.lead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={mutation.isPending || hrMutation.isPending || itMutation.isPending}
            onClick={submit}
          >
            {STR.common.saveChanges}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.editUser.nameLabel}
          </span>
          <input
            className="field"
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.editUser.emailLabel}
          </span>
          <input
            className="field"
            type="email"
            value={email}
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => {
              setEmail(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ display: 'block' }}>
            <span
              className="tx-muted"
              style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
            >
              {STR.modals.editUser.titleLabel}{' '}
              <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                {STR.settings.optional}
              </span>
            </span>
            <input
              className="field"
              type="text"
              value={title}
              maxLength={120}
              placeholder={STR.modals.editUser.titlePlaceholder}
              onChange={(event) => {
                setTitle(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </label>
          <label style={{ display: 'block' }}>
            <span
              className="tx-muted"
              style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
            >
              {STR.modals.editUser.departmentLabel}{' '}
              <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                {STR.settings.optional}
              </span>
            </span>
            <input
              className="field"
              type="text"
              value={department}
              maxLength={120}
              placeholder={STR.modals.editUser.departmentPlaceholder}
              onChange={(event) => {
                setDepartment(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </label>
        </div>
        <label
          style={promotionBlocked ? { ...checkboxRowStyle, cursor: 'not-allowed' } : checkboxRowStyle}
        >
          <input
            type="checkbox"
            checked={isAdmin}
            disabled={promotionBlocked}
            onChange={(event) => {
              setIsAdmin(event.target.checked);
              setError(null);
            }}
            style={checkboxStyle}
          />
          <span>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
              {STR.modals.editUser.adminTitle}
            </span>
            <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
              {STR.modals.editUser.adminNote}
            </span>
            {/*
              The reason, in plain words, right where the sealed control is — the same
              treatment the inert "Reset two-step" control already gets. `tx-muted` rather
              than `tx-subtle`: this one explains a refusal, so it has to be read.
            */}
            {promotionBlocked ? (
              <span
                className="tx-muted"
                style={{ ...hintStyle, marginTop: 6, fontSize: 12, fontWeight: 600 }}
              >
                {STR.modals.editUser.adminInactive}
              </span>
            ) : null}
          </span>
        </label>
        {actorIsAdmin ? (
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={isHr}
              onChange={(event) => {
                setIsHr(event.target.checked);
                setError(null);
              }}
              style={checkboxStyle}
            />
            <span>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
                {STR.access.person.hrTitle}
              </span>
              <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
                {STR.access.person.hrNote}
              </span>
            </span>
          </label>
        ) : null}
        {actorIsAdmin ? (
          <label style={checkboxRowStyle}>
            <input
              type="checkbox"
              checked={isIt}
              onChange={(event) => {
                setIsIt(event.target.checked);
                setError(null);
              }}
              style={checkboxStyle}
            />
            <span>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
                {STR.access.person.itTitle}
              </span>
              <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
                {STR.access.person.itNote}
              </span>
            </span>
          </label>
        ) : null}
      </ModalForm>
    </Modal>
  );
}
